import React from 'react';

/**
 * Keystone by CloudVoro, Brand Identity System
 *
 * Inline SVG logo, scales perfectly at every size without rasterization.
 *
 * Variants:
 *   <KeystoneLogo variant="light" />       - for light backgrounds (cream/white)
 *   <KeystoneLogo variant="dark" />        - for dark backgrounds (navy/black)
 *   <KeystoneLogo variant="mono-dark" />   - single-color navy (mono)
 *   <KeystoneLogo variant="mono-light" />  - single-color white (mono)
 *
 * Layouts:
 *   <KeystoneLogo layout="horizontal" />  - mark + wordmark side-by-side (default)
 *   <KeystoneLogo layout="stacked" />     - mark above wordmark (compact / square)
 *   <KeystoneLogo layout="mark" />        - just the mark icon (favicon-style)
 *
 * Usage:
 *   <KeystoneLogo height={56} variant="light" />
 *   <KeystoneLogo height={48} variant="dark" />
 */

const PALETTE = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  navyMid: '#163E66',
  magenta: '#E91E63',
  magentaDark: '#C2185B',
  gold: '#C9A961',
  white: '#FFFFFF',
  cream: '#FAF8F5',
};

// =============== The Mark (icon) ===============
// Bold "K" monogram in a rounded square
//, modern, corporate, scales perfectly from 16px favicon to 200px hero
//, magenta accent stroke = the "key" element that locks production together
const KeystoneMark = ({ size = 56, variant = 'light' }) => {
  const isDark = variant === 'dark' || variant === 'mono-light';
  const isMono = variant.startsWith('mono');

  const bgFill = isMono
    ? (variant === 'mono-light' ? PALETTE.white : PALETTE.navy)
    : PALETTE.navy;
  const letterFill = isMono
    ? (variant === 'mono-light' ? PALETTE.navy : PALETTE.white)
    : PALETTE.white;
  const accentFill = isMono ? letterFill : PALETTE.magenta;

  // For mono-light variant, swap colors
  const finalBg = variant === 'mono-light' ? PALETTE.white : bgFill;
  const finalLetter = variant === 'mono-light' ? PALETTE.navy : letterFill;
  const finalAccent = variant === 'mono-light' ? PALETTE.navy : accentFill;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', flexShrink: 0, WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
      aria-label="Keystone"
    >
      {/* Rounded square background, solid fill for print fidelity */}
      <rect
        x="2"
        y="2"
        width="96"
        height="96"
        rx="20"
        ry="20"
        fill={finalBg}
      />

      {/* Subtle inner highlight (top edge) for premium feel */}
      {!isMono && (
        <rect
          x="2"
          y="2"
          width="96"
          height="14"
          rx="20"
          ry="20"
          fill="#FFFFFF"
          opacity="0.06"
        />
      )}

      {/* The K letterform, geometric, two-stroke construction */}
      {/* Vertical bar */}
      <rect
        x="24"
        y="22"
        width="14"
        height="56"
        rx="2"
        fill={finalLetter}
      />

      {/* Upper diagonal stroke */}
      <path
        d="M 42 50 L 65 22 L 78 22 L 52 53 Z"
        fill={finalLetter}
      />

      {/* Lower diagonal stroke (in magenta accent) */}
      <path
        d="M 52 47 L 78 78 L 65 78 L 42 50 Z"
        fill={finalAccent}
      />

      {/* Tiny accent dot, the "key" punctuation */}
      {!isMono && (
        <circle
          cx="80"
          cy="78"
          r="3.5"
          fill={PALETTE.gold}
        />
      )}
    </svg>
  );
};

// =============== The Wordmark ===============
const KeystoneWordmark = ({ height = 56, variant = 'light', showTagline = true }) => {
  const isDark = variant === 'dark' || variant === 'mono-light';
  const isMono = variant.startsWith('mono');

  const primaryColor = isMono
    ? (variant === 'mono-light' ? PALETTE.white : PALETTE.navy)
    : (isDark ? PALETTE.white : PALETTE.navy);
  const accentColor = isMono ? primaryColor : PALETTE.magenta;
  const taglineColor = isMono
    ? primaryColor
    : (isDark ? '#94A3B8' : '#718096');

  // Scale the wordmark proportionally
  const scale = height / 56;
  const fontSize = 26 * scale;
  const accentFontSize = 9 * scale;
  const taglineFontSize = 7.5 * scale;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        lineHeight: 1,
        fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div
        style={{
          fontSize: `${fontSize}px`,
          fontWeight: 900,
          color: primaryColor,
          letterSpacing: `${-0.02 * fontSize}px`,
          lineHeight: 0.95,
          textTransform: 'none',
        }}
      >
        Keystone
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: `${0.25 * scale}em`,
          marginTop: `${4 * scale}px`,
        }}
      >
        <span
          style={{
            width: `${10 * scale}px`,
            height: `${2 * scale}px`,
            background: accentColor,
            display: 'inline-block',
          }}
        />
        <span
          style={{
            fontSize: `${accentFontSize}px`,
            fontWeight: 700,
            color: accentColor,
            letterSpacing: `${0.18 * accentFontSize}px`,
            textTransform: 'uppercase',
          }}
        >
          by CloudVoro
        </span>
      </div>
      {showTagline && (
        <div
          style={{
            fontSize: `${taglineFontSize}px`,
            fontWeight: 500,
            color: taglineColor,
            letterSpacing: `${0.12 * taglineFontSize}px`,
            textTransform: 'uppercase',
            marginTop: `${3 * scale}px`,
          }}
        >
          Production Systems for Food Manufacturers
        </div>
      )}
    </div>
  );
};

// =============== Composed logo ===============
export default function KeystoneLogo({
  height = 56,
  variant = 'light',     // 'light' | 'dark' | 'mono-dark' | 'mono-light'
  layout = 'horizontal', // 'horizontal' | 'stacked' | 'mark'
  showTagline = true,
  style = {},
}) {
  if (layout === 'mark') {
    return (
      <div style={style}>
        <KeystoneMark size={height} variant={variant} />
      </div>
    );
  }

  if (layout === 'stacked') {
    return (
      <div
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${height * 0.18}px`,
          ...style,
        }}
      >
        <KeystoneMark size={height * 1.0} variant={variant} />
        <KeystoneWordmark height={height * 0.7} variant={variant} showTagline={showTagline} />
      </div>
    );
  }

  // Default: horizontal
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${height * 0.22}px`,
        ...style,
      }}
    >
      <KeystoneMark size={height} variant={variant} />
      <KeystoneWordmark height={height} variant={variant} showTagline={showTagline} />
    </div>
  );
}

export { KeystoneMark, KeystoneWordmark };
